import React, { FC, useState } from 'react';

import { themeLight } from '@digital-hig/mui/Theme';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { config } from './config';
import { ConfigContextProvider } from './contexts/ConfigContext';
import { I18nContextProvider } from './contexts/I18nContext';
import { LoginContextProvider } from './contexts/LoginContext';
import { MarketingParametersContextProvider } from './contexts/MarketingParametersContext';
import type { MarketingParameter } from './contexts/MarketingParametersContext';
import { ToastContextProvider } from './contexts/ToastContext';
import { ConfigInterface } from '../common/interface';

// @ts-expect-error - MUI / DHIG issue with theme compatibility
const lightTheme = createTheme(themeLight);

export interface AURRProviderProps {
  language: string;
  children?: React.ReactNode;
  appConfig?: Partial<ConfigInterface>;
  marketingParameters?: MarketingParameter[];
}

export const EmotionAURRCache = createCache({
  key: 'aurr',
});

export function AURRProvider({
  marketingParameters,
  language,
  children,
  appConfig,
}: AURRProviderProps) {
  // Instead do this, which ensures each request has its own cache:
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
          },
        },
      })
  );

  return (
    <CacheProvider value={EmotionAURRCache}>
      <MarketingParametersContextProvider
        marketingParameters={marketingParameters}
      >
        <ConfigContextProvider appConfig={{ ...config, ...appConfig }}>
          <QueryClientProvider client={queryClient}>
            <LoginContextProvider>
              {/* DEV ONLY: uncomment and import following line only during local development */}
              {/* <ReactQueryDevtools initialIsOpen={false} /> */}
              <I18nContextProvider languageRequested={language}>
                <ThemeProvider theme={lightTheme}>
                  <ToastContextProvider>{children}</ToastContextProvider>
                </ThemeProvider>
              </I18nContextProvider>
            </LoginContextProvider>
          </QueryClientProvider>
        </ConfigContextProvider>
      </MarketingParametersContextProvider>
    </CacheProvider>
  );
}

export const WithAURRContext = (
  OriginalComponent: FC<Partial<AURRProviderProps>>
) =>
  function EnrichedComponent({
    language,
    appConfig,
    marketingParameters,
    ...props
  }: AURRProviderProps) {
    return (
      <AURRProvider
        marketingParameters={marketingParameters}
        language={language}
        appConfig={appConfig}
      >
        <OriginalComponent {...props} />
      </AURRProvider>
    );
  };
